import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import { useHippoText } from '../../../../hooks/useHippoText';
import type {
    PrhHolidayHomeOnsaDTO,
    PrhHouseOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/house';
import { useMemo, useState } from 'react';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import { useRecoilState, useRecoilValue } from 'recoil';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { prhBBRAtom } from '../../../../recoil/atoms/prhBBRAtom';
import HouseBBRBackModal from './HouseBBRBackModal';
import _ from 'lodash';
import { isFormValid } from './HouseBBRUtil/BBRValidation';
import HouseBBREditForm from './HouseBBREditForm';
import ViewInformationModal from '../../../../shared_components/ViewInformationModal/ViewInformationModal';
import { COVERABLE_CODE } from '../../../../constants/coverable';

const HouseBBREditInformation = ({
    goPrevious,
    setIsEditingBBR
}: {
    goPrevious: () => void;
    setIsEditingBBR: React.Dispatch<React.SetStateAction<boolean>>;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const [bbrData, setBbrData] = useRecoilState(prhBBRAtom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const cloneBbrData = useMemo(() => _.cloneDeep(bbrData), []);
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);

    const [isLoading, setIsLoading] = useState(false);
    const [showBackModal, setShowBackModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [didEdit, setDidEdit] = useState(false);

    const onConfirm = (): void => {
        setShowConfirmModal(true);
        setIsLoading(true);
        setBbrData((prevBBR) => ({ ...prevBBR, isBBRComplete: true }));
        const houseType =
            currentProduct.flavourCode === COVERABLE_CODE.prhHouse ? 'house' : 'holidayHome';
        const saveHouseDTO = {
            publicId: bbrData.publicId,
            fixedId: bbrData.fixedId,
            constructionYear: bbrData.constructionYear,
            developedArea: bbrData.developedArea,
            basement: bbrData.basement === 'Ja',
            garageOuthouseArea: bbrData.garageOuthouseArea,
            numberOfFloorsId: bbrData.numberOfFloorsOptions?.find(
                (option) => option.displayName === bbrData.numberOfFloors
            )?.code,
            exteriorWallId: bbrData.exteriorWallTypeOptions?.find(
                (option) => option.displayName === bbrData.exteriorWallType
            )?.code,
            roofTypeId: bbrData.roofTypeOptions?.find(
                (option) => option.displayName === bbrData.roofType
            )?.code,
            primaryHeatingId: bbrData.primaryHeatingTypeOptions?.find(
                (option) => option.displayName === bbrData.primaryHeatingType
            )?.code,
            numberOfToilets: bbrData.numberOfToilets,
            listedWorthPreservingId: bbrData.listedWorthPreservingTypeOptions?.find(
                (option) => option.displayName === bbrData.listedWorthPreservingType
            )?.code,
            closedDownFarm: bbrData.closedDownFarm,
            closedDownCommercial: bbrData.closedDownCommercial
        };
        if (currentProduct.flavourCode === COVERABLE_CODE.prhHouse) {
            const updatedCoverable: CoverableInformationToUpdateDTO = {
                [houseType]: {
                    ...saveHouseDTO,
                    livingArea: (bbrData as PrhHouseOnsaDTO).livingArea,
                    basementArea: (bbrData as PrhHouseOnsaDTO).basementArea,
                    commercialActivitiesInTheBuildingId: (
                        bbrData as PrhHouseOnsaDTO
                    ).commercialActivitiesInTheBuildingOptions?.find(
                        (option) =>
                            option.displayName ===
                            (bbrData as PrhHouseOnsaDTO).commercialActivitiesInTheBuilding
                    )?.code
                },
                productToUpdate: bbrData.flavourName
            };
            void saveCoverableInformation(updatedCoverable).then(() => {
                setIsLoading(false);
            });
        } else {
            const updatedCoverable: CoverableInformationToUpdateDTO = {
                [houseType]: {
                    ...saveHouseDTO,
                    fireplace: (bbrData as PrhHolidayHomeOnsaDTO).fireplace === 'Ja'
                },
                productToUpdate: bbrData.flavourName
            };
            void saveCoverableInformation(updatedCoverable).then(() => {
                setIsLoading(false);
            });
        }
    };

    const onPrevious = (): void => {
        if (!didEdit && !isFormValid(bbrData)) {
            setBbrData(() => cloneBbrData);
            goPrevious();
        } else if (didEdit && isFormValid(bbrData)) {
            setShowBackModal(true);
        } else if (didEdit && !isFormValid(bbrData)) {
            setBbrData(() => cloneBbrData);
            if (bbrData.isBBRComplete) {
                setIsEditingBBR(false);
            } else {
                goPrevious();
            }
        } else {
            setIsEditingBBR(false);
        }
    };

    return (
        <WizardPagetemplate
            onNext={onConfirm}
            onPrevious={onPrevious}
            continueButtonText={getHippoText('DSS-COVERABLE-INFORMATION-STEP-BBR-SAVE')}
            disableNext={isLoading || !isFormValid(bbrData)}
        >
            <CoverableDetailsTemplate
                header={
                    bbrData.isBBRComplete
                        ? getHippoText(
                              'DSS-COVERABLE-INFORMATION-STEP-BBR-CORRECT-INFORMATION-HEADER'
                          )
                        : getHippoText('DSS-COVERABLE-INFORMATION-STEP-BBR-INSUFFICIENT-HEADER')
                }
                subheader={
                    bbrData.isBBRComplete
                        ? getHippoText(
                              'DSS-COVERABLE-INFORMATION-STEP-BBR-CORRECT-INFORMATION-SUBHEADER'
                          )
                        : getHippoText('DSS-COVERABLE-INFORMATION-STEP-BBR-INSUFFICIENT-SUBHEADER')
                }
            >
                <HouseBBREditForm setDidEdit={setDidEdit} />
            </CoverableDetailsTemplate>
            <ViewInformationModal
                closeModal={() => {
                    setShowConfirmModal(false);
                    setIsEditingBBR(false);
                }}
                modalProps={{
                    title: getHippoText('DSS-COVERABLE-INFORMATION-STEP-BBR-CONFIRM-TITLE'),
                    description: getHippoText('DSS-COVERABLE-INFORMATION-STEP-BBR-CONFIRM-SUBTITLE')
                }}
                showModal={showConfirmModal}
            />
            <HouseBBRBackModal
                showModal={showBackModal}
                saveAndCloseModal={() => {
                    setShowBackModal(false);
                    onConfirm();
                }}
                cancelAndCloseModal={() => {
                    setBbrData(() => cloneBbrData);
                    setShowBackModal(false);
                    if (bbrData.isBBRComplete) {
                        setIsEditingBBR(false);
                    } else {
                        goPrevious();
                    }
                }}
            />
        </WizardPagetemplate>
    );
};

export default HouseBBREditInformation;
